import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import api, { formatApiErrorDetail } from "@/lib/api";
import { buildWhatsAppLink, normalizeWhatsAppPhone } from "@/lib/whatsappUtils";
import TemplateFields, { useTemplateForm, firstName } from "@/components/conversations/TemplateFields";
import { FOLLOWUPS } from "@/constants/testIds";

function fillTemplate(template, treatment, clinicName) {
  return (template || "")
    .replaceAll("{patient_name}", treatment?.patient_name || "")
    .replaceAll("{service_name}", treatment?.service_name || "tu tratamiento")
    .replaceAll("{clinic_name}", clinicName || "");
}

/** Contactar por WhatsApp desde Seguimiento: numero final visible antes de
 * enviar (con heuristica +1 EEUU) y, al enviar, marca el seguimiento como
 * "contactada" en un solo paso.
 * Fix O (2026-09-30): con WhatsApp de Meta activo, el mensaje sale desde el
 * numero de la clinica con una plantilla aprobada y queda en Conversaciones.
 * Si el canal todavia no es Meta (antes del cambio de numero), abre WhatsApp
 * como antes. */
export default function FollowUpWhatsAppModal({ open, onOpenChange, treatment, template, clinicName, onSuccess }) {
  const [phone, setPhone] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const who = firstName(treatment?.patient_name);
  const service = treatment?.service_name || "";
  const form = useTemplateForm(open, "seguimiento", { nombre: who, tratamiento: service, 1: who, 2: service });

  useEffect(() => {
    if (open && treatment) {
      setPhone(treatment.patient_phone || "");
      setMessage(fillTemplate(template, treatment, clinicName));
      setError("");
    }
  }, [open, treatment, template, clinicName]);

  if (!treatment) return null;

  const finalNumber = normalizeWhatsAppPhone(phone);
  const viaLink = form.status === "unavailable";
  const canSend = !sending && phone.trim() && (viaLink || form.complete);

  const handleSend = async () => {
    setSending(true);
    setError("");
    try {
      let notes = message;
      if (viaLink) {
        window.open(buildWhatsAppLink(phone, message), "_blank");
      } else {
        await api.post("/conversations/start", {
          phone,
          name: treatment.patient_name,
          template: form.template.name,
          language: form.template.language,
          params: form.values,
        });
        notes = form.text;
      }
      const { data } = await api.post(`/followups/${treatment.id}/contact`, { notes });
      onSuccess(data);
      onOpenChange(false);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid={FOLLOWUPS.whatsappModal}>
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">Contactar por WhatsApp</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <p className="text-sm text-muted-foreground">
            Paciente: <span className="text-foreground font-medium">{treatment.patient_name}</span>
          </p>
          <div>
            <Label className="text-sm text-muted-foreground mb-1 block">Numero de WhatsApp</Label>
            <Input value={phone} onChange={(e) => setPhone(e.target.value)} data-testid={FOLLOWUPS.whatsappPhoneInput} />
            <p className="text-xs text-muted-foreground mt-1">
              Se va a enviar a: <span className="font-medium">{finalNumber ? `+${finalNumber}` : "numero invalido"}</span>
            </p>
          </div>
          {viaLink ? (
            <div>
              <Label className="text-sm text-muted-foreground mb-1 block">Mensaje</Label>
              <Textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={5} data-testid={FOLLOWUPS.whatsappMessageInput} />
            </div>
          ) : (
            <TemplateFields form={form} />
          )}
          {error && <p className="text-sm text-destructive">{error}</p>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid={FOLLOWUPS.whatsappCancelButton}>Cancelar</Button>
          <Button onClick={handleSend} disabled={!canSend} data-testid={FOLLOWUPS.whatsappSendButton}>
            {sending ? "Enviando..." : viaLink ? "Abrir WhatsApp y marcar contactada" : "Enviar por WhatsApp y marcar contactada"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
